/**
 * Tablature: which string a note is played on, and where the finger stops it.
 *
 * A tab staff draws no pitches at all. Its lines are the strings of the instrument, from
 * the lowest at the bottom, and on each line stands the number of the fret. So a note on a
 * tab staff is the ordinary note it would be on any other staff -- a pitch, a duration, a
 * tie, a slur -- with one thing added: where on the instrument it is taken. That is
 * {@link TabPosition}, and it sits on {@link NotePitch} beside the pitch rather than
 * replacing it, which is what lets the same part be drawn as notation, as tab, or as both.
 *
 * The pitch stays the truth and the fret is written down rather than worked out, because
 * the same pitch can be taken in five places on a guitar and which one a player uses is a
 * decision about the hand, not about the note. A position that disagrees with its pitch is
 * therefore kept as it was written: a file from elsewhere opens as its author meant it.
 *
 * ## Strings are numbered as MusicXML numbers them
 *
 * String 1 is the highest-sounding string -- the thin E of a guitar -- and the numbers
 * count downwards from it. That is the players' numbering as well as the format's, and it
 * is the opposite of the staff line order, so {@link tuningLine} is the one place the two
 * are turned into each other.
 *
 * ## The tuning is written pitch
 *
 * A guitar is written an octave above where it sounds, and its tuning is stated in the same
 * space its notes are -- E3 A3 D4 G4 B4 E5 for a part written that way -- so that a fret is
 * a plain subtraction from the note on the page and no layer has to cross the part's
 * transposition to find one. A reader that takes the tuning for sounding pitch still gets
 * every fret right, because frets travel in the file as themselves.
 */

import { midiOf, pitchOf, spell } from "./pitch";
import type { NotePitch, Step } from "./pitch";

/** One open string of a fretted instrument, in the pitch space the part is written in. */
export interface StringTuning {
  readonly step: Step;
  /** Semitones of accidental, as a pitch's: a string tuned down a semitone is -1. */
  readonly alter: number;
  readonly octave: number;
}

/** Where a note is taken on the instrument. */
export interface TabPosition {
  /** String number, 1 for the highest-sounding string. */
  readonly string: number;
  /** Fret, 0 for the open string. */
  readonly fret: number;
}

/** The highest fret this package will choose on its own; a player may write a higher one. */
export const HIGHEST_FRET = 24;

function tuned(place: string): StringTuning {
  return { step: place[0] as Step, alter: 0, octave: Number(place.slice(1)) };
}

/**
 * Standard guitar tuning, written an octave above where it sounds, string 1 first.
 *
 * The octave is the part's, not the instrument's: a guitar part is written in the treble
 * clef an octave high by every publisher, so its open strings are written there too.
 */
export const GUITAR_TUNING: readonly StringTuning[] = ["E5", "B4", "G4", "D4", "A3", "E3"].map(
  tuned,
);

/** Standard four-string bass tuning, written an octave above where it sounds. */
export const BASS_TUNING: readonly StringTuning[] = ["G3", "D3", "A2", "E2"].map(tuned);

/** Standard ukulele tuning, with the re-entrant fourth string a sounding G above the rest. */
export const UKULELE_TUNING: readonly StringTuning[] = ["A4", "E4", "C4", "G4"].map(tuned);

/** The open string, or `null` when the instrument has no string of that number. */
export function openString(tuning: readonly StringTuning[], string: number): StringTuning | null {
  return tuning[string - 1] ?? null;
}

/** What the open string sounds, as a MIDI number in the part's written space. */
export function openMidi(tuning: readonly StringTuning[], string: number): number | null {
  const open = openString(tuning, string);
  return open === null ? null : midiOf(pitchOf(open.step, open.alter, open.octave));
}

/**
 * The staff line a string is drawn on, counted from the bottom line as 1.
 *
 * The lowest string is the bottom line, so the two numberings are mirrors of each other.
 * This is also how MusicXML numbers `<staff-tuning line="…">`.
 */
export function tuningLine(tuning: readonly StringTuning[], string: number): number {
  return tuning.length - string + 1;
}

/**
 * The fret that takes this pitch on that string, or `null` when the string cannot reach it.
 *
 * A string cannot play below its open pitch, and nothing above {@link HIGHEST_FRET} is
 * chosen for a player who did not ask for it.
 */
export function fretFor(
  tuning: readonly StringTuning[],
  string: number,
  pitch: NotePitch,
): number | null {
  const open = openMidi(tuning, string);
  if (open === null) return null;
  const fret = midiOf(pitch) - open;
  return fret < 0 || fret > HIGHEST_FRET ? null : fret;
}

/**
 * Where a pitch falls on the instrument by itself: the lowest fret that takes it.
 *
 * This is the first guess a tab staff makes when a note arrives with no position of its
 * own, and it is the one a player corrects. The lowest fret means the highest string that
 * can reach the note, which keeps a line in first position instead of sending it up the
 * neck, and a re-entrant string (the ukulele's fourth) is searched with the rest rather
 * than assumed to be the lowest.
 *
 * `taken` are the strings already spoken for, which is how a chord is placed: one hand
 * cannot stop two of its notes on the same string, so a head added to a chord has to go
 * somewhere its other heads are not.
 */
export function positionFor(
  tuning: readonly StringTuning[],
  pitch: NotePitch,
  taken: ReadonlySet<number> = new Set(),
): TabPosition | null {
  let best: TabPosition | null = null;
  for (let string = 1; string <= tuning.length; string += 1) {
    if (taken.has(string)) continue;
    const fret = fretFor(tuning, string, pitch);
    if (fret === null) continue;
    if (best === null || fret < best.fret) best = { string, fret };
  }
  return best;
}

/**
 * The pitch a fret sounds, spelled for the key, or `null` when there is no such string.
 *
 * Typing a fret number is how a tab staff is written, so the note has to be given a pitch
 * nobody stated. The key decides sharps or flats, which is the same rule a MIDI keyboard's
 * notes are spelled by.
 */
export function pitchAt(
  tuning: readonly StringTuning[],
  string: number,
  fret: number,
  fifths: number,
): NotePitch | null {
  const open = openMidi(tuning, string);
  if (open === null || fret < 0) return null;
  return { ...spell(open + fret, fifths), tab: { string, fret } };
}
